import { useEffect, useMemo, useState } from "react";
import { AlertColor, Typography, Link } from "@mui/material";
import { ImportStatus } from "../../Enums/ImportStatus";
import { useNotification } from "./notificationHooks";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useImportCandidateCSVMutation, useImportStatus } from "../../Queries/ImportQueries";
import { useImportStatusStore } from "../Stores/ImportStatusStore";
import { useShallow } from "zustand/react/shallow";

export function useImportUploadResult() {
  const {
    statusId,
    isInputError,
    isUploadError,
    isValidId: checkValidId,
  } = useImportStatusStore(
    useShallow((state) => ({
      statusId: state.statusId,
      isInputError: state.isInputError,
      isUploadError: state.isUploadError,
      isValidId: state.isValidId,
    })),
  );

  const [isValidId, setIsValidId] = useState(false);

  const { data: importStatus } = useImportStatus(statusId ?? "");

  const isSuccessImport = useMemo(
    () =>
      importStatus?.status === ImportStatus.Completed ||
      importStatus?.status === ImportStatus.CompletedWithConflicts ||
      importStatus?.status === ImportStatus.CompletedWithFailures,
    [importStatus],
  );
  const isFailedImport = useMemo(() => importStatus?.status === ImportStatus.Failed, [importStatus]);

  useEffect(() => {
    setIsValidId(checkValidId());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusId]);

  return { isInputError, isUploadError, isValidId, isSuccessImport, isFailedImport } as const;
}

// Show import status text or notification based on the page and current import status
export function useImportStatusNotifications() {
  const {
    statusId,
    showImportModal,
    isInputError,
    isUploadError,
    showImportStatusText,
    showImportStatusNotification,
    setShowImportStatusText,
    setShowImportStatusNotification,
    reset,
  } = useImportStatusStore(
    useShallow((state) => ({
      statusId: state.statusId,
      showImportModal: state.showImportModal,
      isInputError: state.isInputError,
      isUploadError: state.isUploadError,
      showImportStatusText: state.showImportStatusText,
      showImportStatusNotification: state.showImportStatusNotification,
      setShowImportStatusText: state.setShowImportStatusText,
      setShowImportStatusNotification: state.setShowImportStatusNotification,
      reset: state.reset,
    })),
  );

  const { isValidId, isSuccessImport, isFailedImport } = useImportUploadResult();
  const { isLoading } = useImportCandidateCSVMutation();

  const { pathname } = useLocation();
  const { displayUpload, clearNotification } = useNotification();
  const [isCandidateImportPage, setIsCandidateImportPage] = useState(false);
  const [alertColor, setAlertColor] = useState("info" as AlertColor);
  const [notificationTimeout, setNotificationTimeout] = useState(Infinity);
  const [message, setMessage] = useState<string | JSX.Element>("Import processing...");

  const shouldDisplayMessaging = useMemo(
    () => !isInputError && !isUploadError && !showImportModal && isValidId,
    [isInputError, isUploadError, isValidId, showImportModal],
  );

  function clearStatusNotification() {
    clearNotification();
    reset();
  }

  useEffect(() => {
    setIsCandidateImportPage(pathname.includes("/candidates") || pathname.includes("/candidategroups/"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (isSuccessImport && statusId) {
      setAlertColor("success");
      setNotificationTimeout(3000);
      setMessage(
        <>
          <Typography>Successful CSV import, view details </Typography>
          <Link color="inherit" component={RouterLink} to={`/candidates/imports?importId=${statusId}`}>
            View Imports
          </Link>
        </>,
      );
    } else if (isFailedImport) {
      setAlertColor("error");
      setNotificationTimeout(6000);
      setMessage(
        <>
          <Typography>Error importing candidates </Typography>
          <Link color="inherit" component={RouterLink} to={`/candidates/imports`}>
            View Imports
          </Link>
        </>,
      );
    } else if (isLoading) {
      setAlertColor("info");
      setNotificationTimeout(Infinity);
      setMessage("Import processing...");
    }
  }, [isSuccessImport, isFailedImport, statusId, isLoading]);

  // If modal is not showing, and import is Created or In Progress, show notification when not on the
  // candidate import page or text when on the candidate import page, else hide the notification/text
  useEffect(() => {
    if (shouldDisplayMessaging) {
      if (!isCandidateImportPage) {
        setShowImportStatusNotification(true);
      } else {
        clearStatusNotification();
      }

      if (isCandidateImportPage) {
        setShowImportStatusText(true);
      } else {
        clearStatusNotification();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCandidateImportPage, shouldDisplayMessaging]);

  // When message is shown, set a timeout to hide it
  useEffect(() => {
    let setStatusTimeout: ReturnType<typeof setTimeout>;

    if (shouldDisplayMessaging) {
      if (showImportStatusNotification) {
        displayUpload(message, notificationTimeout, alertColor);
      }
      setStatusTimeout = setTimeout(() => {
        clearStatusNotification();
      }, notificationTimeout);
    }

    return () => {
      clearTimeout(setStatusTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldDisplayMessaging,
    showImportStatusText,
    showImportStatusNotification,
    alertColor,
    message,
    notificationTimeout,
  ]);
}
